import {
  WebinarAnalyticsDetailSchema, WebinarAnalyticsListSchema,
  type WebinarAnalyticsDetail, type WebinarAnalyticsList, type WebinarCodeWordPurpose, type WebinarGroup, type WebinarHistory,
  type WebinarDeals, type WebinarOutcomeCounts, type WebinarSegment,
} from "@vekha/contracts";

import { ApiRequestError } from "./api";
import { formatNumber, pluralRu } from "./campaigns-format";

type Parser<T> = Readonly<{ parse: (value: unknown) => T }>;

function fallback(status: number): string {
  if (status === 403) return "Аналитика эфиров доступна руководителю.";
  if (status === 404) return "Эфир не найден — возможно, его удалили из календаря.";
  if (status === 401) return "Сессия закончилась. Войдите снова.";
  return "Аналитика сейчас недоступна. Повторите через минуту.";
}

async function read<T>(parser: Parser<T>, path: string): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`/api/calendar/webinars${path}`, {
      credentials: "include", signal: AbortSignal.timeout(40_000), headers: { Accept: "application/json" },
    });
  } catch {
    throw new ApiRequestError("Сервер не ответил. Проверьте соединение и повторите.");
  }
  let payload: unknown = null;
  try { payload = await response.json(); } catch { payload = null; }
  if (!response.ok) {
    const error = (payload as { error?: { message?: string; code?: string } } | null)?.error;
    throw new ApiRequestError(response.status === 503 && error?.message ? error.message : fallback(response.status),
      { status: response.status, ...(error?.code ? { code: error.code } : {}) });
  }
  return parser.parse(payload);
}

export function fetchWebinarAnalyticsList(from: string, to: string): Promise<WebinarAnalyticsList> {
  return read(WebinarAnalyticsListSchema, `/analytics?${new URLSearchParams({ from, to })}`);
}

/** One day of a series (or a lone event): its numbers, with the series around it. */
export function fetchWebinarAnalytics(eventId: string): Promise<WebinarAnalyticsDetail> {
  return read(WebinarAnalyticsDetailSchema, `/${encodeURIComponent(eventId)}/analytics`);
}

/** A whole series: its days and the дожим. */
export function fetchWebinarSeriesAnalytics(seriesKey: string): Promise<WebinarAnalyticsDetail> {
  return read(WebinarAnalyticsDetailSchema, `/series/${encodeURIComponent(seriesKey)}/analytics`);
}

// «Эфиры» is a section of its own next to «Рассылки» (Паша 30.09.2026); the old calendar addresses redirect here.
export const seriesPath = (key: string) => `/analytics/webinars/series/${encodeURIComponent(key)}`;
export const eventPath = (eventId: string) => `/analytics/webinars/events/${eventId}`;
const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/u;
/** The month a `?month=` asks for, when it is a real one. */
export function webinarsMonth(value: string | null): string | null {
  return value !== null && MONTH.test(value) ? value : null;
}
/** The webinar list of the month of `date` (`YYYY-MM-DD` or `YYYY-MM`); the current month without one. */
export function webinarsPath(date?: string | null): string {
  const month = webinarsMonth(date?.slice(0, 7) ?? null);
  return month ? `/webinars?month=${month}` : "/webinars";
}

/**
 * Where an old calendar address of a webinar leads now: `/calendar/series/:key` and `/calendar/events/:id` keep their
 * query (the people filter), «Календарь → Эфиры» (`/calendar?tab=events&date=…`) opens the list of that month.
 * Links to them stay in people's chats and agents' notes.
 */
export function legacyWebinarTarget(pathname: string, search: string): string | null {
  const page = /^\/calendar\/(series|events)\/([^/]+)\/?$/u.exec(pathname);
  if (page) return `/webinars/${page[1]}/${page[2]}${search}`;
  const params = new URLSearchParams(search);
  if (/^\/calendar\/?$/u.test(pathname) && params.get("tab") === "events") return webinarsPath(params.get("date"));
  return null;
}

/** «22–24 сен» for a span of days, «22 сен» for one. */
export function dayRange(first: string, last: string): string {
  if (first === last) return shortDay(first);
  if (first.slice(0, 7) === last.slice(0, 7)) return `${Number(first.slice(8))}–${shortDay(last)}`;
  return `${shortDay(first)} — ${shortDay(last)}`;
}

// ————— Words the page uses —————

export const GROUP_LABELS: Readonly<Record<WebinarGroup, string>> = {
  new_deal: "Новые", repeat_request: "Повторные", outside_sales: "Не в отделе продаж",
};
export const GROUP_SHORT: Readonly<Record<WebinarGroup, string>> = {
  new_deal: "новая сделка", repeat_request: "повторная заявка", outside_sales: "не в отделе продаж",
};
/** Segments by the person's facts before the series (Ден 21.10.2026, core webinar-page.series.v8). */
export const SEGMENT_LABELS: Readonly<Record<WebinarSegment, string>> = {
  new: "Новые", warm: "Тёплые", back: "Возвращаются", client: "Уже клиенты",
};
/** One fact under the name: why the person is in the segment. */
export const SEGMENT_HINTS: Readonly<Record<WebinarSegment, string>> = {
  new: "впервые у нас", warm: "были на консультации", back: "заявка была, консультации нет", client: "уже покупали",
};
export const SEGMENT_WORDS: Readonly<Record<WebinarSegment, [string, string, string]>> = {
  new: ["новый", "новых", "новых"], warm: ["тёплый", "тёплых", "тёплых"], back: ["возвращается", "возвращаются", "возвращаются"], client: ["клиент", "клиента", "клиентов"],
};
export const SEGMENT_COLORS: Readonly<Record<WebinarSegment, string>> = {
  new: "var(--webinar-seg-new)", warm: "var(--webinar-seg-warm)", back: "var(--webinar-seg-back)", client: "var(--webinar-seg-client)",
};
export const PURPOSE_LABELS: Readonly<Record<WebinarCodeWordPurpose, string>> = {
  application: "Заявка", bonus: "Бонус", follow_up: "Дожим", offer: "Оффер",
};
/** How «old» a repeat request is (the analytics' groups). */
export const HISTORY_LABELS: Readonly<Record<WebinarHistory, string>> = {
  bought: "Покупка уже была", consulted: "Консультация была, покупки нет", returns: "Приходили 2 раза и больше, без консультации", first_return: "Приходили 1 раз, без консультации",
};

/** «новые 22 · повторные 19 · не в отделе продаж 3», zero groups left out. */
export function splitLabel(counts: Pick<WebinarOutcomeCounts, "new" | "repeat" | "outside">): string {
  return [["новые", counts.new], ["повторные", counts.repeat], ["не в отделе продаж", counts.outside]]
    .filter(([, value]) => Number(value) > 0).map(([label, value]) => `${label} ${value}`).join(" · ");
}

/** «2026-03-29» → «мар 2026». */
export function monthYear(date: string): string {
  const text = new Intl.DateTimeFormat("ru-RU", { timeZone: "Europe/Moscow", month: "short", year: "numeric" }).format(new Date(`${date}T12:00:00Z`));
  return text.replace(/\.?\s*г\.?$/u, "").replace(/\./gu, "").replace("сент", "сен").replace("нояб", "ноя").replace("февр", "фев");
}

const euro = new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 0 });
/** «6 810 €»; cents only when there are any. */
export function formatEuro(value: number): string {
  const rounded = Math.round(value * 100) / 100;
  const text = Number.isInteger(rounded) ? euro.format(rounded)
    : new Intl.NumberFormat("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(rounded);
  return `${text} €`;
}

/** «3,1 тыс €» where a column is narrow. */
/** «ещё ждём 5 600 €», and «без суммы N» when some deals have no sum: the line under «сумма сделок». */
export function dealsNote(deals: WebinarDeals): string {
  // Non-breaking inside each part: a narrow column wraps between them, not inside.
  return `ещё ждём\u00a0${formatEuro(deals.outstanding)}${deals.withoutSum > 0 ? ` · без\u00a0суммы\u00a0${formatNumber(deals.withoutSum)}` : ""}`;
}

export function formatEuroShort(value: number): string {
  if (value < 1000) return `${Math.round(value)} €`;
  const thousands = Math.round(value / 100) / 10;
  return `${new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 1 }).format(thousands)}k €`;
}

export function peopleCount(count: number): string {
  return pluralRu(count, "человек", "человека", "человек");
}

export function sentenceCase(text: string): string {
  return text ? text[0]!.toLocaleUpperCase("ru-RU") + text.slice(1) : text;
}

const MOSCOW = "Europe/Moscow";
const dayMonth = new Intl.DateTimeFormat("ru-RU", { timeZone: MOSCOW, day: "numeric", month: "short" });
const time = new Intl.DateTimeFormat("ru-RU", { timeZone: MOSCOW, hour: "2-digit", minute: "2-digit" });

/** «22 сент., 21:10» → «22 сен, 21:10»: short months without the dot. */
export function moscowMoment(iso: string): string {
  const at = new Date(iso);
  return `${shortMonth(dayMonth.format(at))}, ${time.format(at)}`;
}

/** «2026-09-23» → «23 сен». */
export function shortDay(date: string): string {
  return shortMonth(dayMonth.format(new Date(`${date}T12:00:00Z`)));
}

function shortMonth(text: string): string {
  return text.replace(/\.$/u, "").replace("сент", "сен").replace("нояб", "ноя").replace("февр", "фев");
}

export function weekdayShort(date: string): string {
  return new Intl.DateTimeFormat("ru-RU", { timeZone: MOSCOW, weekday: "short" }).format(new Date(`${date}T12:00:00Z`));
}

/** «данные на 30 сен, 05:21» and whether they are older than half a day. */
export function freshness(dataAsOf: string | null, now: Date = new Date()): { label: string; stale: boolean } | null {
  if (!dataAsOf) return null;
  const at = Date.parse(dataAsOf);
  if (!Number.isFinite(at)) return null;
  return { label: `данные на ${moscowMoment(dataAsOf)} МСК`, stale: now.getTime() - at > 12 * 3_600_000 };
}
